import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  inputBaseClasses,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { CaseViewers } from "../cases/CaseViewers";
import { ChevronRight, Clear } from "@mui/icons-material";
import { Transition } from "../../pages/app/utils/animation";
import { useCase } from "../../store/case/hooks";
import { useModals } from "../../store/dialogs/hooks";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const EditCaseSchema = z.object({
  caseName: z.string().trim().nonempty({ message: "A case name is required." }),
  caseFileNumber: z.string().trim().optional(),
  fieldOffice: z.string().trim().optional(),
  description: z.string().trim().optional(),
  viewAll: z.boolean().optional(),
  viewersList: z
    .array(
      z.object({
        key: z.number(),
        label: z.string(),
        avatar: z.string(),
      }),
    )
    .optional(),
});

export const EditCaseMetaDataDialog = ({ af_case, readOnly }) => {
  const { updateMetadata } = useCase();
  const { caseDetailsCaseId, showCaseDetailsDialog, closeCaseDetailsDialog } =
    useModals();

  const initialState = useMemo(
    () => ({
      description: af_case?.description ?? "",
      caseName: af_case?.caseName ?? "",
      caseFileNumber: af_case?.caseFileNumber ?? "",
      fieldOffice: af_case?.fieldOffice ?? "",
      id: af_case?.id,
      viewAll: af_case?.viewAll ?? false,
      viewersList: af_case?.viewersList ?? [],
      comment: af_case?.comment ?? "",
    }),
    [af_case],
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    resolver: zodResolver(EditCaseSchema),
    values: { ...initialState } || {},
    mode: "all",
  });

  const viewAll = watch("viewAll");

  const onSubmit = async (data) => {
    await updateMetadata({
      id: af_case.id,
      ...data,
    });
    reset();
    closeCaseDetailsDialog();
  };

  const handleClose = () => {
    reset();
    closeCaseDetailsDialog();
  };

  const handleNameClear = () => {
    setValue("caseName", "", { shouldDirty: true });
    trigger("caseName");
  };

  return (
    <Dialog
      aria-hidden={!showCaseDetailsDialog}
      open={showCaseDetailsDialog && caseDetailsCaseId === af_case.id}
      onClose={handleClose}
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {`${readOnly ? "View" : "Edit"}`} Case Details
        </DialogTitle>
        <DialogContent dividers>
          <Grid container mt={1} spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="caseName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Case Name"
                    error={!!errors.caseName}
                    helperText={
                      errors.caseName
                        ? errors.caseName?.message
                        : "Please enter a name for the case."
                    }
                    required
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{
                              [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]:
                                {
                                  opacity: 0,
                                  pointerEvents: "none",
                                },
                            }}
                          >
                            <IconButton
                              aria-label="clear input"
                              onClick={handleNameClear}
                              edge="end"
                              size="small"
                            >
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        ),
                        readOnly: readOnly,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="caseFileNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Case Number"
                    slotProps={{ input: { readOnly: readOnly } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="fieldOffice"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Field Office"
                    slotProps={{ input: { readOnly: readOnly } }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Case Description"
                    slotProps={{ input: { readOnly: readOnly } }}
                  />
                )}
              />
            </Grid>
            <Grid mt={3} size={{ xs: 12 }}>
              <Typography variant="h6">Team (Optional)</Typography>
            </Grid>
            <Grid display="flex" alignItems="center" size={{ xs: 12 }}>
              <Controller
                name="viewAll"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        disabled={readOnly}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        slotProps={{
                          input: {
                            "aria-label": "toggle all users",
                            role: "switch",
                          },
                        }}
                      />
                    }
                    label="All Users"
                  />
                )}
              />
            </Grid>
            <Collapse in={!viewAll} sx={{ width: 1 }}>
              <Grid size={{ xs: 12 }}>
                <CaseViewers
                  control={control}
                  name="viewersList"
                  readOnly={readOnly}
                />
              </Grid>
            </Collapse>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            endIcon={<ChevronRight />}
            disabled={!isDirty || !isValid || readOnly}
            variant="contained"
            type="submit"
          >
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
